/**
 * Levels: eleven of them, L0 (intern) to L10 (C-suite), shared with jobs.
 *
 * The API's cbt_levels rows carry `code`; a talent's userCbtLevel carries
 * `level_code` and `level_confirmed`. The old junior / mid / senior words
 * survive only as bands, derived here exactly as the API derives them
 * (droomworkApi/app/Services/Levels.js), so nothing has to store them.
 */

export type LevelBand = "junior" | "mid" | "senior";

const LEGACY_TO_CODE: Record<string, number> = { junior: 2, mid: 3, senior: 4 };

type LevelLike = { level_code?: number | string | null; level_name?: string | null; code?: number | string | null; name?: string | null } | null | undefined;

/** 0..10 from whatever the row holds, or null. Old rows only have the word. */
export function levelCode(level: LevelLike): number | null {
  if (!level) return null;
  const raw = level.level_code ?? level.code;
  if (raw !== undefined && raw !== null && raw !== "") {
    const n = Number(raw);
    if (Number.isInteger(n) && n >= 0 && n <= 10) return n;
  }
  const word = String(level.level_name ?? level.name ?? "").trim().toLowerCase();
  return word in LEGACY_TO_CODE ? LEGACY_TO_CODE[word] : null;
}

export function levelBand(level: LevelLike): LevelBand | null {
  const code = levelCode(level);
  if (code === null) return null;
  if (code <= 2) return "junior";
  if (code === 3) return "mid";
  return "senior";
}

/** What used to be `level_name === "mid" || level_name === "senior"`. */
export const isMidOrAbove = (level: LevelLike) => {
  const band = levelBand(level);
  return band === "mid" || band === "senior";
};

/** "L4 · Senior" (or just the name for a row without a code). */
export function levelLabel(level: LevelLike): string {
  if (!level) return "";
  const name = level.level_name ?? level.name ?? "";
  const code = levelCode(level);
  return code === null ? String(name) : `L${code} · ${name}`;
}

/** The migration mapped existing talents to a code and left this false until they pick a level themselves. */
export const levelNeedsConfirmation = (level: { level_confirmed?: boolean | number | null } | null | undefined) =>
  !!level && (level.level_confirmed === false || level.level_confirmed === 0);
